if (!customElements.get('brass-customizer')) {
  customElements.define('brass-customizer', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.controller = new AbortController();
      this.previewMode = this.dataset.previewMode === 'true';
      this.extrasVisited ??= false;
      this.submitting = false;
      this.panels = [...this.querySelectorAll('[data-panel]')];
      this.steps = [...this.querySelectorAll('[data-step-button]')];
      this.uploads = [...this.querySelectorAll('frame-photo-upload')];
      this.extrasStep = Number(this.dataset.extrasStep);
      this.form.noValidate = true;
      this.classList.add('is-enhanced');
      this.querySelector('[data-production-property]').disabled = false;
      const options = { signal: this.controller.signal };
      this.mobileMedia = this.dataset.mobileContinuous === 'true' ? window.matchMedia('(max-width: 59.99rem)') : null;
      this.mobileMedia?.addEventListener('change', () => {
        const activePanel = document.activeElement?.closest('[data-panel]');
        const step = activePanel && this.contains(activePanel) ? Number(activePanel.dataset.panel) : this.step;
        this.showStep(step, false);
      }, options);
      window.addEventListener('pageshow', () => {
        this.setSubmitting(false);
        this.update();
      }, options);
      this.addEventListener('click', event => this.handleClick(event), options);
      this.form.addEventListener('frame-upload-change', () => this.update(), options);
      this.form.addEventListener('input', event => this.handleInput(event), options);
      this.form.addEventListener('change', event => this.handleInput(event), options);
      this.form.addEventListener('keydown', event => {
        if (event.key !== 'Enter' || event.isComposing || !event.target.matches('[data-engraving]')) return;
        event.preventDefault();
        if (this.continuous) {
          const fields = [...this.querySelectorAll('[data-engraving]:not(:disabled)')];
          const next = fields[fields.indexOf(event.target) + 1] || this.panels[1].querySelector('h3');
          next.focus();
          return;
        }
        this.form.requestSubmit();
      }, options);
      this.form.addEventListener('submit', event => this.handleSubmit(event), options);
      this.form.addEventListener('reset', () => {
        queueMicrotask(() => {
          if (!this.controller) return;
          this.extrasVisited = false;
          this.setSubmitting(false);
          this.uploads.forEach(upload => upload.resetUploads());
          this.panels.forEach(panel => this.showError(Number(panel.dataset.panel), ''));
          this.update();
          this.showStep(1, false);
        });
      }, options);
      this.update();
      this.showStep(Number(this.dataset.step) || 1, false);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
    }

    handleClick(event) {
      const button = event.target.closest('button');
      if (!button || !this.contains(button)) return;
      if (button.hasAttribute('data-step-button') || button.hasAttribute('data-edit')) {
        this.showStep(Number(button.dataset.stepButton || button.dataset.edit));
      } else if (button.hasAttribute('data-next')) {
        if (this.validate(this.step)) this.showStep(this.step + 1);
      } else if (button.hasAttribute('data-back')) {
        this.showStep(this.step - 1);
      }
    }

    handleInput(event) {
      if (event.target.closest('frame-photo-upload')) return;
      if (event.target.matches('[data-production]')) this.extrasVisited = true;
      this.update();
    }

    handleSubmit(event) {
      if (this.previewMode) event.preventDefault();
      if (this.submitting) {
        event.preventDefault();
        return;
      }
      if (!this.continuous && this.step < this.panels.length) {
        event.preventDefault();
        if (this.validate(this.step)) this.showStep(this.step + 1);
        return;
      }
      if (!this.validateAll()) {
        event.preventDefault();
        return;
      }
      if (this.previewMode) {
        this.querySelector('[data-preview-notice]')?.focus();
        return;
      }
      if (event.defaultPrevented) return;
      this.setSubmitting(true);
      queueMicrotask(() => {
        if (event.defaultPrevented && this.controller) this.setSubmitting(false);
      });
    }

    setSubmitting(submitting) {
      this.submitting = submitting;
      if (submitting) this.form.setAttribute('aria-busy', 'true');
      else this.form.removeAttribute('aria-busy');
      const add = this.querySelector('[data-add]');
      if (!add) return;
      const available = this.isAvailable(this.querySelector('[data-production]:checked'));
      add.disabled = submitting || !available;
      add.textContent = submitting ? add.dataset.addingLabel : available ? add.dataset.addLabel : add.dataset.soldOutLabel;
    }

    showStep(step, focus = true) {
      this.continuous = Boolean(this.mobileMedia?.matches);
      this.classList.toggle('is-continuous', this.continuous);
      this.step = Math.min(this.panels.length, Math.max(1, step));
      if (this.step === this.extrasStep) {
        this.extrasVisited = true;
        this.update();
      }
      this.dataset.step = String(this.step);
      this.panels.forEach(panel => { panel.hidden = !this.continuous && Number(panel.dataset.panel) !== this.step; });
      this.querySelector('.brass-navigation').hidden = this.continuous;
      this.querySelector('.brass-heading > span').hidden = this.continuous;
      this.querySelector('.brass-review').hidden = this.continuous;
      this.steps.forEach(button => {
        if (!this.continuous && Number(button.dataset.stepButton) === this.step) button.setAttribute('aria-current', 'step');
        else button.removeAttribute('aria-current');
      });
      const panel = this.panels[this.step - 1];
      const previewPanel = this.continuous ? this.panels[0] : panel;
      this.querySelector('.brass-preview').hidden = !this.continuous && this.dataset.scene === 'brass' && this.step > 1;
      this.text('[data-preview-location]', previewPanel.dataset.location);
      const layout = this.querySelector('[data-layout-preview]');
      const showLayout = Boolean(layout && previewPanel.dataset.panelKey !== 'engraving');
      this.querySelector('[data-plaque-preview]').hidden = showLayout;
      if (layout) layout.hidden = !showLayout;
      this.querySelectorAll('[data-area]').forEach(area => { area.hidden = area.dataset.area !== previewPanel.dataset.panelKey; });
      const placement = this.querySelector('[data-placement-note]');
      if (placement) placement.hidden = showLayout;
      this.text('[data-preview-label]', showLayout ? this.dataset.layoutLabel : this.dataset.plaqueLabel);
      this.text('[data-preview-caption]', showLayout ? this.dataset.layoutCaption : this.dataset.plaqueCaption);
      const pointer = this.querySelector('[data-preview-pointer]');
      pointer.hidden = this.continuous || panel.dataset.panelKey !== 'engraving';
      pointer.textContent = String(this.step).padStart(2, '0');
      this.querySelector('[data-progress-fill]').style.width = `${this.step / this.panels.length * 100}%`;
      this.querySelector('[role="progressbar"]').setAttribute('aria-valuenow', String(this.step));
      this.text('[data-progress-step]', this.dataset.stepTemplate.replace('[step]', this.step));
      this.text('[data-progress-remaining]', this.step === this.panels.length
        ? this.dataset.lastStep
        : this.dataset.remainingTemplate.replace('[count]', this.panels.length - this.step));
      this.querySelector('[data-back]').hidden = this.continuous || this.step === 1;
      this.querySelector('[data-next]').hidden = this.continuous || this.step === this.panels.length;
      this.querySelector('[data-finish]').hidden = !this.continuous && this.step !== this.panels.length;
      if (focus) {
        panel.querySelector('h3').focus({ preventScroll: true });
        if (this.continuous) {
          panel.scrollIntoView({ block: 'start', behavior: 'instant' });
          return;
        }
        const navigation = this.querySelector('.brass-navigation');
        const preview = this.querySelector('.brass-preview');
        if (navigation.getBoundingClientRect().top < 0 || preview.getBoundingClientRect().bottom > window.innerHeight) {
          navigation.scrollIntoView({ block: 'start', behavior: 'instant' });
        }
      }
    }

    update() {
      ['upper', 'lower'].forEach(key => {
        const field = this.querySelector(`[data-engraving="${key}"]`);
        const blank = this.querySelector(`[data-blank="${key}"]`).checked;
        field.disabled = blank;
        this.querySelector(`[data-blank-property="${key}"]`).disabled = !blank;
        this.text(`[data-count="${key}"]`, `${field.value.length} / ${field.maxLength}`);
        const value = field.value.trim();
        const preview = this.querySelector(`[data-preview-${key}]`);
        preview.textContent = blank ? '' : value || field.placeholder;
        const weight = [...preview.textContent].reduce((total, letter) => total +
          (/[^\u0000-\u00ff]/.test(letter) ? 1 : /[MWmw@%]/.test(letter) ? 0.95 : /[A-Z0-9]/.test(letter) ? 0.7 : 0.55), 0);
        const size = Math.min(24, 208 / Math.max(1, weight));
        const text = preview.closest('text');
        text.setAttribute('font-size', size.toFixed(1));
        text.style.fontSize = `${size.toFixed(1)}px`;
        this.text(`[data-review="${key}"]`, blank ? this.dataset.noEngraving : value || this.dataset.emptyLabel);
      });
      const production = this.querySelector('[data-production]:checked');
      if (production) {
        const label = production.closest('label').querySelector('strong').textContent;
        const productionTime = production.closest('label').querySelector(':scope > span > span').textContent.trim();
        if (!this.previewMode) {
          this.text('.brass-total [data-total]', production.dataset.total);
          this.text('[data-total-note]', label);
        }
        this.text('[data-review-production]', `${label} — ${productionTime}`);
        this.querySelector('[data-production-property]').value = `${label} — ${productionTime}`;
        const product = this.closest('.product');
        const price = product?.querySelector('[data-price]');
        const priceLabel = product?.querySelector('[data-price-label]');
        const makeTime = product?.querySelector('[data-brass-make-time]');
        if (price && !this.previewMode) price.textContent = production.dataset.total;
        if (priceLabel && !this.previewMode) priceLabel.textContent = label;
        if (makeTime) makeTime.textContent = productionTime;
        if (!this.previewMode) this.dispatchEvent(new CustomEvent('seremori:variant-change', { bubbles: true, detail: { money: production.dataset.total, price: Number(production.dataset.priceCents), comparePrice: Number(production.dataset.compareCents), compareMoney: production.dataset.compareMoney } }));
      }
      this.setSubmitting(this.submitting);
      this.uploads.forEach(upload => {
        const files = upload.files || [];
        this.text(`[data-review-upload="${upload.dataset.kind}"]`, upload.sendLater ? upload.dataset.laterLabel : files.length ? files.map(file => file.name).join('\n') : this.dataset.emptyLabel);
      });
      this.steps.forEach(button => {
        const step = Number(button.dataset.stepButton);
        const complete = !this.errorFor(step) && (step !== this.extrasStep || this.extrasVisited);
        button.classList.toggle('is-complete', complete);
        button.querySelector('[data-step-status]').textContent = complete ? ` — ${this.dataset.completeLabel}` : '';
        const error = this.querySelector(`[data-error="${step}"]`);
        if (error && !error.hidden) this.showError(step, this.errorFor(step));
      });
    }

    errorFor(step) {
      const panel = this.panels[step - 1];
      const key = panel.dataset.panelKey;
      if (key === 'engraving') {
        return [...panel.querySelectorAll('[data-engraving]')].every(field => field.disabled || field.value.trim() && field.value.length <= field.maxLength) ? '' : this.dataset.requiredError;
      }
      const upload = panel.querySelector('frame-photo-upload');
      if (upload) return upload.errorMessage ?? upload.dataset.requiredError;
      if (key === 'extras') return this.isAvailable(this.querySelector('[data-production]:checked')) ? '' : this.dataset.unavailableError;
      return '';
    }

    showError(step, message) {
      const upload = this.panels[step - 1].querySelector('frame-photo-upload');
      if (upload) {
        if (message) upload.validate?.();
        return;
      }
      const error = this.querySelector(`[data-error="${step}"]`);
      if (error) {
        error.textContent = message;
        error.hidden = !message;
      }
      const panel = this.panels[step - 1];
      panel.querySelectorAll('input:not([type="hidden"]):not([type="checkbox"])').forEach(field => {
        if (message) field.setAttribute('aria-invalid', 'true');
        else field.removeAttribute('aria-invalid');
      });
    }

    validate(step) {
      const error = this.errorFor(step);
      this.showError(step, error);
      if (error) {
        this.showStep(step, false);
        const panel = this.panels[step - 1];
        const invalidEngraving = [...panel.querySelectorAll('[data-engraving]')]
          .find(field => !field.disabled && (!field.value.trim() || field.value.length > field.maxLength));
        const target = invalidEngraving || panel.querySelector('[data-upload-picker]') || panel.querySelector('input:not([type="hidden"]):not(:disabled)');
        target?.focus();
      }
      return !error;
    }

    validateAll() {
      this.update();
      for (let step = 1; step <= this.panels.length; step++) {
        if (!this.validate(step)) return false;
      }
      return true;
    }

    isAvailable(production) {
      if (this.previewMode) return Boolean(production);
      return Boolean(production && production.value && !production.hasAttribute('data-unlinked') && production.dataset.available === 'true');
    }

    text(selector, value) {
      const element = this.querySelector(selector);
      if (element) element.textContent = value || '';
    }
  });
}
